/**
 * Intake Wizard Shared Components
 * Reusable form components for the AURA Intake Wizard
 */

import React from 'react';
import { Plus, X, Trash, CaretDown, CaretUp, Upload, Check, Info } from '@phosphor-icons/react';

// ============================================================
// TEXT FIELD
// ============================================================

export const TextField = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  required, 
  type = 'text', 
  helpText,
  testId,
  disabled = false 
}) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-text-secondary">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testId}
    />
    {helpText && <p className="text-xs text-text-tertiary">{helpText}</p>}
  </div>
);

// ============================================================
// SELECT FIELD
// ============================================================

export const SelectField = ({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder, 
  required,
  testId,
  disabled = false 
}) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-text-secondary">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testId}
    >
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(opt => (
        <option key={opt.id || opt.value || opt} value={opt.id || opt.value || opt}>
          {opt.name || opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

// ============================================================
// TEXTAREA FIELD
// ============================================================

export const TextAreaField = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  required, 
  rows = 3,
  helpText,
  testId 
}) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-text-secondary">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 transition-all resize-none"
      data-testid={testId}
    />
    {helpText && <p className="text-xs text-text-tertiary">{helpText}</p>}
  </div>
);

// ============================================================
// CHECKBOX FIELD
// ============================================================

export const CheckboxField = ({ 
  label, 
  checked, 
  onChange, 
  description,
  testId 
}) => (
  <label className="flex items-start gap-3 cursor-pointer group" data-testid={testId}>
    <div className={`mt-0.5 w-5 h-5 rounded border ${checked ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'} flex items-center justify-center transition-all`}>
      {checked && <Check size={14} weight="bold" className="text-primary-foreground" />}
    </div>
    <div>
      <span className="text-sm text-text-primary">{label}</span>
      {description && <p className="text-xs text-text-tertiary mt-0.5">{description}</p>}
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
  </label>
);

// ============================================================
// TOGGLE FIELD
// ============================================================

export const ToggleField = ({ 
  label, 
  checked, 
  onChange, 
  description,
  testId 
}) => (
  <div className="flex items-center justify-between p-4 bg-background-surface rounded-lg border border-border" data-testid={testId}>
    <div>
      <span className="text-sm font-medium text-text-primary">{label}</span>
      {description && <p className="text-xs text-text-tertiary mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-border'}`}
    >
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-7' : 'translate-x-1'}`} />
    </button>
  </div>
);

// ============================================================
// MULTI-SELECT CHIPS
// ============================================================

export const MultiSelectChips = ({ 
  label, 
  options, 
  selected = [], 
  onChange, 
  maxDisplay = 10,
  testId 
}) => {
  const toggleItem = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };
  
  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="text-sm font-medium text-text-secondary">{label}</label>}
      <div className="flex flex-wrap gap-2">
        {options.slice(0, maxDisplay).map(opt => {
          const id = opt.id || opt.value || opt;
          const name = opt.name || opt.abbreviation || opt.label || opt;
          const isSelected = selected.includes(id);
          
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggleItem(id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isSelected 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-background-surface border border-border text-text-secondary hover:border-primary/50'
              }`}
              data-testid={`${testId}-${id}`}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// COLLAPSIBLE SECTION
// ============================================================

export const CollapsibleSection = ({ 
  title, 
  children, 
  defaultOpen = false,
  icon: Icon,
  testId 
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-background-surface flex items-center justify-between text-left hover:bg-background transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-text-secondary" />}
          <span className="text-sm font-medium text-text-primary">{title}</span>
        </div>
        {isOpen ? <CaretUp size={18} className="text-text-tertiary" /> : <CaretDown size={18} className="text-text-tertiary" />}
      </button>
      {isOpen && (
        <div className="p-4 border-t border-border bg-background">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================
// STEP SECTION
// ============================================================

export const StepSection = ({ 
  title, 
  description, 
  icon: Icon, 
  children 
}) => (
  <div className="space-y-6 py-6">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon size={20} className="text-primary" />
        </div>
      )}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        {description && <p className="text-sm text-text-secondary">{description}</p>}
      </div>
    </div>
    <div className="space-y-4">
      {children}
    </div>
  </div>
);

// ============================================================
// DYNAMIC LIST (for adding multiple items)
// ============================================================

export const DynamicList = ({ 
  label, 
  items = [], 
  onAdd, 
  onRemove, 
  renderItem, 
  addButtonText = 'Add Item',
  maxItems = 20,
  testId 
}) => (
  <div className="space-y-3" data-testid={testId}>
    {label && <label className="text-sm font-medium text-text-secondary">{label}</label>}
    
    {items.map((item, index) => (
      <div key={item.id || index} className="relative p-4 bg-background-surface rounded-lg border border-border">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="absolute top-2 right-2 p-1 text-text-tertiary hover:text-red-400 transition-colors"
        >
          <Trash size={16} />
        </button>
        {renderItem(item, index)}
      </div>
    ))}
    
    {items.length < maxItems && (
      <button
        type="button"
        onClick={onAdd}
        className="w-full py-3 border-2 border-dashed border-border rounded-lg text-text-secondary hover:border-primary/50 hover:text-primary transition-all flex items-center justify-center gap-2"
      >
        <Plus size={18} />
        {addButtonText}
      </button>
    )}
  </div>
);

// ============================================================
// FILE UPLOAD
// ============================================================

export const FileUpload = ({ 
  label, 
  accept, 
  onUpload, 
  uploading = false,
  uploadedFile,
  helpText,
  testId 
}) => (
  <div className="space-y-2" data-testid={testId}>
    {label && <label className="text-sm font-medium text-text-secondary">{label}</label>}
    
    {uploadedFile ? (
      <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
        <Check size={20} className="text-emerald-500" />
        <span className="text-sm text-text-primary flex-1 truncate">{uploadedFile.name}</span>
        <button
          type="button"
          onClick={() => onUpload(null)}
          className="p-1 text-text-tertiary hover:text-red-400 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    ) : (
      <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
        <Upload size={32} className="text-text-tertiary mb-2" />
        <span className="text-sm text-text-secondary">
          {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
        </span>
        <span className="text-xs text-text-tertiary mt-1">{accept || 'PDF, PNG, JPG up to 10MB'}</span>
        <input
          type="file"
          accept={accept}
          onChange={(e) => onUpload(e.target.files?.[0])}
          className="sr-only"
          disabled={uploading}
        />
      </label>
    )}
    
    {helpText && <p className="text-xs text-text-tertiary">{helpText}</p>}
  </div>
);

// ============================================================
// INFO CARD
// ============================================================

export const InfoCard = ({ 
  title, 
  children, 
  variant = 'info' // info, warning, success
}) => {
  const variants = {
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  };
  
  return (
    <div className={`p-4 rounded-lg border ${variants[variant]}`}>
      <div className="flex items-start gap-2">
        <Info size={18} className="mt-0.5 flex-shrink-0" />
        <div>
          {title && <p className="text-sm font-medium">{title}</p>}
          <div className="text-sm opacity-90">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STEP INDICATOR
// ============================================================

export const StepIndicator = ({ 
  steps, 
  currentStep, 
  completedSteps 
}) => (
  <div className="flex items-center justify-center gap-1 mb-8 flex-wrap" data-testid="step-indicator">
    {steps.map((step, idx) => {
      const stepNum = idx + 1;
      const isCompleted = completedSteps.includes(stepNum);
      const isCurrent = idx === currentStep;
      
      return (
        <div key={idx} className="flex items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all cursor-pointer ${
              isCurrent 
                ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background' 
                : isCompleted 
                  ? 'bg-emerald-500 text-white'
                  : 'bg-background-surface border border-border text-text-tertiary hover:border-primary/50'
            }`}
            title={step.title}
            data-testid={`step-${stepNum}-indicator`}
            data-step-completed={isCompleted}
            data-step-current={isCurrent}
          >
            {isCompleted && !isCurrent ? <Check size={14} weight="bold" /> : stepNum}
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-4 h-0.5 mx-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-border'}`} />
          )}
        </div>
      );
    })}
  </div>
);
